// MegaphoneCompose — client/host modal for posting an announcement to
// every student registered for a specific competition. Stripped to two
// fields (title + body) per product direction. On submit it creates the
// row, immediately publishes it, and surfaces in:
//   - Each student's Activity overlay (header bell)
//   - The program dashboard's Announcements card
//   - Each team's chat as a green inline event (See more → overlay)

import { useEffect, useState } from "react";
import { Modal as RNModal, Platform, Pressable, Text, TextInput, View, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";

import { captainSendAnnouncement } from "../../lib/announcements";
import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const THEME = PANE_THEMES.Client;
const FOREST = THEME.sidebarBg;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE_DEEP.hex;
const SAND_DARK = COLORS.SAND_DARK.hex;
const TEXT = THEME.sidebarBg;
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_FAINT = "rgba(28,43,26,0.42)";

export type MegaphoneComposeProps = {
  visible: boolean;
  competitionId: string;
  competitionName?: string | null;
  onClose: () => void;
  /** Fired after a successful publish so the parent can refresh its
   *  announcements list / show a toast. */
  onPublished?: () => void;
};

export function MegaphoneCompose({
  visible,
  competitionId,
  competitionName,
  onClose,
  onPublished,
}: MegaphoneComposeProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open so the modal never re-shows last session's draft.
  useEffect(() => {
    if (visible) {
      setTitle("");
      setBody("");
      setBusy(false);
      setError(null);
    }
  }, [visible]);

  const canSend = !busy && title.trim().length > 0;

  const onSend = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      // Captain path — RPC enforces "caller is captain of a team in this
      // competition" server-side and inserts an auto-published, comp-scoped
      // announcement. Sidesteps the announcements RLS write policy which
      // otherwise restricts inserts to admins.
      await captainSendAnnouncement(competitionId, title.trim(), body.trim());
      onPublished?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send announcement.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <RNModal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={[
          {
            flex: 1,
            backgroundColor: "rgba(14,14,14,0.50)",
            alignItems: "center",
            justifyContent: "center",
            paddingHorizontal: 24,
          },
          Platform.OS === "web"
            ? ({
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                cursor: "default",
              } as object)
            : null,
        ]}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={[
            {
              width: "100%",
              maxWidth: 520,
              backgroundColor: THEME.textOnSurface,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: SAND_DARK,
              overflow: "hidden",
            },
            Platform.OS === "web" ? ({ cursor: "default" } as unknown as object) : null,
          ]}
        >
          {/* Header */}
          <View
            style={{
              padding: 14,
              paddingHorizontal: 18,
              borderBottomWidth: 1,
              borderBottomColor: SAND_DARK,
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
            }}
          >
            <View
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                backgroundColor: "rgba(100,127,77,0.14)",
                borderWidth: 1,
                borderColor: "rgba(100,127,77,0.55)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Svg width={14} height={14} viewBox="0 0 20 20" fill="none">
                <Path
                  d="M3 8 L3 12 L6 12 L13 16 L13 4 L6 8 Z"
                  fill={SAGE}
                  stroke={SAGE}
                  strokeWidth={0.5}
                  strokeLinejoin="round"
                />
                <Path
                  d="M15 7 Q17 10 15 13"
                  stroke={SAGE}
                  strokeWidth={1.4}
                  fill="none"
                  strokeLinecap="round"
                />
              </Svg>
            </View>
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  fontFamily: FONTS.displayMedium,
                  fontSize: 17,
                  color: FOREST,
                }}
              >
                New announcement
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 11,
                  color: TEXT_MUTED,
                }}
                numberOfLines={1}
              >
                {competitionName ?? "Send to all students in this program"}
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              style={({ hovered }: { hovered?: boolean }) => [
                {
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor:
                    hovered && Platform.OS === "web" ? "rgba(28,43,26,0.32)" : "rgba(28,43,26,0.18)",
                  alignItems: "center",
                  justifyContent: "center",
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
                <Path
                  d="M2 2 L10 10 M10 2 L2 10"
                  stroke={FOREST}
                  strokeWidth={1.4}
                  strokeLinecap="round"
                />
              </Svg>
            </Pressable>
          </View>

          {/* Body */}
          <View style={{ padding: 18, gap: 14 }}>
            <View>
              <Text style={labelStyle}>Title</Text>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder="e.g. Workshop tomorrow at 4pm"
                placeholderTextColor={TEXT_FAINT}
                maxLength={140}
                style={inputStyle}
              />
            </View>
            <View>
              <Text style={labelStyle}>Body</Text>
              <TextInput
                value={body}
                onChangeText={setBody}
                placeholder="Optional details. Long body? It'll truncate in chat with a "
                placeholderTextColor={TEXT_FAINT}
                multiline
                maxLength={2000}
                style={[inputStyle, { minHeight: 90, textAlignVertical: "top" }]}
              />
            </View>

            {error ? (
              <Text
                style={{
                  fontFamily: FONTS.bodyMedium,
                  fontSize: 12,
                  color: THEME.destructive,
                }}
              >
                {error}
              </Text>
            ) : null}

            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                gap: 10,
                paddingTop: 4,
              }}
            >
              <Pressable
                onPress={onClose}
                disabled={busy}
                style={({ hovered }: { hovered?: boolean }) => [
                  {
                    paddingHorizontal: 16,
                    paddingVertical: 9,
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor:
                      hovered && Platform.OS === "web" ? "rgba(28,43,26,0.40)" : "rgba(28,43,26,0.20)",
                  },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={primaryLabelStyle("ghost")}>CANCEL</Text>
              </Pressable>
              <Pressable
                onPress={() => void onSend()}
                disabled={!canSend}
                style={({ hovered }: { hovered?: boolean }) => [
                  {
                    paddingHorizontal: 16,
                    paddingVertical: 9,
                    borderRadius: 4,
                    backgroundColor: canSend
                      ? hovered && Platform.OS === "web"
                        ? THEME.accentDeep
                        : AMBER
                      : "rgba(181,135,58,0.40)",
                  },
                  Platform.OS === "web" ? ({ cursor: canSend ? "pointer" : "not-allowed" } as unknown as ViewStyle) : null,
                ]}
              >
                <Text style={primaryLabelStyle("primary")}>{busy ? "SENDING…" : "SEND ANNOUNCEMENT"}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

const labelStyle = {
  fontFamily: FONTS.bodyMedium,
  fontSize: 11,
  color: TEXT_MUTED,
  marginBottom: 4,
  letterSpacing: 0.4,
} as const;

const inputStyle = {
  backgroundColor: COLORS.WHITE.hex,
  borderWidth: 1,
  borderColor: SAND_DARK,
  borderRadius: 4,
  paddingHorizontal: 12,
  paddingVertical: 10,
  fontFamily: FONTS.body,
  fontSize: 13,
  color: TEXT,
  outlineWidth: 0 as unknown as number,
} as const;

function primaryLabelStyle(tone: "primary" | "ghost") {
  return {
    fontFamily: FONTS.bodyBold,
    fontSize: 11,
    letterSpacing: 1.2,
    color: tone === "primary" ? FOREST : TEXT,
  };
}
